

import { MapPin, Zap, Clock, WalletCards, Car, ShieldCheck, Plug } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import StarRating from '@/components/StarRating';

interface StationInfoProps {
  title: string;
  location: string;
  rating?: number;
  reviews?: any[];
  powerOutput: number;
  available: boolean;
  chargerType: string;
  availableHours?: string;
  isHomeCharger: boolean;
  amenities?: string[];
  // New props
  networkOperator?: string;
  plugTypes?: string[];
  numberOfPorts?: number;
  parkingType?: string;
  paymentTypes?: string[];
  status?: string;
  verified?: boolean;
  costDetails?: {
    basePrice: number;
    minPrice?: number;
    maxPrice?: number;
    parkingFee?: number;
    idleFee?: number;
    perMinute?: number;
    perKwh?: number;
    flatRate?: number;
  };
}

const StationInfo = ({ 
  title, 
  location, 
  rating, 
  reviews, 
  powerOutput, 
  chargerType,
  availableHours,
  amenities,
  networkOperator,
  plugTypes,
  numberOfPorts,
  parkingType,
  paymentTypes,
  status,
  verified,
  costDetails
}: StationInfoProps) => {
  return (
    <div>
      <h1 className="text-3xl font-bold">{title}</h1>
      <div className="flex items-center mt-2 text-muted-foreground">
        <MapPin className="h-4 w-4 mr-1" />
        <span>{location}</span>
      </div>

      <div className="flex flex-wrap items-center gap-4 mt-4">
        {rating !== undefined && (
          <div className="flex items-center">
            <StarRating rating={rating} totalRatings={reviews?.length} size={20} />
          </div>
        )}
        
        <Badge variant="outline" className="flex items-center gap-1">
          <Zap className="h-3 w-3 text-blue-500" />
          <span>{powerOutput} kW</span>
        </Badge>
        
        <Badge variant={status === 'operational' ? "default" : "destructive"}>
          {status === 'operational' ? "Available" : status}
        </Badge>
        
        <Badge variant="secondary" className="capitalize">
          {chargerType} Charger
        </Badge>

        {verified && (
          <Badge variant="outline" className="flex items-center gap-1">
            <ShieldCheck className="h-3 w-3 text-green-500" />
            <span>Verified</span>
          </Badge>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h2 className="text-xl font-semibold mb-4">Charger Details</h2>
          <div className="space-y-3">
            {networkOperator && (
              <div className="flex items-center gap-2">
                <Plug className="h-4 w-4 text-blue-500" />
                <span>Network: {networkOperator}</span>
              </div>
            )}
            
            {plugTypes && plugTypes.length > 0 && (
              <div>
                <span className="font-medium">Plug Types: </span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {plugTypes.map((type, index) => (
                    <Badge key={index} variant="outline">{type}</Badge>
                  ))}
                </div>
              </div>
            )}
            
            {numberOfPorts && (
              <div>
                <span className="font-medium">Number of Ports: </span>
                <span>{numberOfPorts}</span>
              </div>
            )}

            {parkingType && (
              <div className="flex items-center gap-2">
                <Car className="h-4 w-4" />
                <span>Parking: {parkingType}</span>
              </div>
            )}
          </div>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-4">Pricing & Payment</h2>
          <div className="space-y-3">
            {costDetails && (
              <div>
                <div className="flex items-center gap-2">
                  <WalletCards className="h-4 w-4 text-green-500" />
                  <span className="font-medium">Base Rate: ${costDetails.basePrice}/kWh</span>
                </div>
                {costDetails.idleFee && (
                  <div className="text-sm text-muted-foreground mt-1">
                    Idle Fee: ${costDetails.idleFee}/min
                  </div>
                )}
              </div>
            )}

            {paymentTypes && paymentTypes.length > 0 && (
              <div>
                <span className="font-medium">Accepted Payment: </span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {paymentTypes.map((type, index) => (
                    <Badge key={index} variant="outline">{type}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {availableHours && (
        <div className="mt-4 flex items-center text-muted-foreground">
          <Clock className="h-4 w-4 mr-2" />
          <span>Available: {availableHours}</span>
        </div>
      )}

      <div className="mt-6">
        <h2 className="text-xl font-semibold mb-4">Amenities</h2>
        {amenities && amenities.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {amenities.map((amenity, index) => (
              <div key={index} className="flex items-center">
                <span className="h-2 w-2 rounded-full bg-primary mr-2"></span>
                <span>{amenity}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground">No amenities listed for this station.</p>
        )}
      </div>
    </div>
  );
};

export default StationInfo;
